import { action, computed, makeObservable, observable, runInAction } from "mobx"
import type { Address } from "viem"

import type { IStorageBackend, IUrlParams } from "@/domains/common/storage"
import { LcAddress } from "@/domains/core"
import type { TokenStore } from "@/domains/tokens"
import { ETH, SCALE, WETH } from "@/domains/tokens/static-tokens"

import type { ISwapInputStore, ITokenLookup } from "./interfaces"
import type { TokenParamSources } from "./types"

/**
 * Swap Input Store
 * Manages input token, output token, and amount
 * Uses MobX for reactivity
 */
export class SwapInputStore implements ISwapInputStore {
  fromTokenParamSources: TokenParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: ETH.address.toViemAddress(),
    ui: undefined,
  }
  toTokenParamSources: TokenParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: SCALE.address.toViemAddress(),
    ui: undefined,
  }
  inputAmount: string = ""

  constructor(
    private readonly storage: IStorageBackend,
    private readonly urlParams: IUrlParams,
    private readonly chainSlug: string,
    private readonly tokenRepository: ITokenLookup,
    private readonly nativeToken: TokenStore,
  ) {
    makeObservable(this, {
      fromTokenParamSources: observable,
      toTokenParamSources: observable,
      inputAmount: observable,
      fromToken: computed,
      toToken: computed,
      inputToken: computed,
      outputToken: computed,
      initialize: action,
      initializeFromUrlParams: action,
      setFromTokenFromUrlParam: action,
      setFromTokenFromLocalStorage: action,
      setFromTokenFromUi: action,
      setToTokenFromUrlParam: action,
      setToTokenFromLocalStorage: action,
      setToTokenFromUi: action,
      setInputToken: action,
      setOutputToken: action,
      setInputAmount: action,
      switchTokens: action,
    })
  }

  initialize(): void {
    runInAction(() => {
      this.initializeFromStorage()
      this.initializeFromUrlParams()
    })
  }

  get inputToken(): TokenStore | undefined {
    return this.resolveToken(this.fromToken)
  }

  get outputToken(): TokenStore | undefined {
    return this.resolveToken(this.toToken)
  }

  get fromToken(): Address | undefined {
    return (
      this.fromTokenParamSources.ui ??
      this.fromTokenParamSources.urlParam ??
      this.fromTokenParamSources.localstorage ??
      this.fromTokenParamSources.default
    )
  }

  get toToken(): Address | undefined {
    return (
      this.toTokenParamSources.ui ??
      this.toTokenParamSources.urlParam ??
      this.toTokenParamSources.localstorage ??
      this.toTokenParamSources.default
    )
  }

  private initializeFromStorage(): void {
    const localFromToken = this.storage.getItem(`${this.chainSlug}-swap-from-address`)
    if (localFromToken) {
      this.setFromTokenFromLocalStorage(localFromToken)
    }

    const localToToken = this.storage.getItem(`${this.chainSlug}-swap-to-address`)
    if (localToToken) {
      this.setToTokenFromLocalStorage(localToToken)
    }
  }

  initializeFromUrlParams(): void {
    const urlSellToken = this.urlParams.getParam("sell")
    if (urlSellToken) {
      this.setFromTokenFromUrlParam(urlSellToken)
    }

    const urlBuyToken = this.urlParams.getParam("buy")
    if (urlBuyToken) {
      this.setToTokenFromUrlParam(urlBuyToken)
    }
  }

  setFromTokenFromUrlParam(value: string | undefined): void {
    if (!value) return
    if (this.fromTokenParamSources.urlParam === value) return
    this.fromTokenParamSources.urlParam = value as Address
  }

  setFromTokenFromLocalStorage(value: string | undefined): void {
    if (!value) return
    if (this.fromTokenParamSources.localstorage === value) return
    this.fromTokenParamSources.localstorage = value as Address
  }

  setFromTokenFromUi(value: string | undefined): void {
    if (!value) return
    if (this.fromTokenParamSources.ui === value) return
    this.fromTokenParamSources.ui = value as Address
    this.persistFromToken(value)
  }

  setToTokenFromUrlParam(value: string | undefined): void {
    if (!value) return
    if (this.toTokenParamSources.urlParam === value) return
    this.toTokenParamSources.urlParam = value as Address
  }

  setToTokenFromLocalStorage(value: string | undefined): void {
    if (!value) return
    if (this.toTokenParamSources.localstorage === value) return
    this.toTokenParamSources.localstorage = value as Address
  }

  setToTokenFromUi(value: string | undefined): void {
    if (!value) return
    if (this.toTokenParamSources.ui === value) return
    this.toTokenParamSources.ui = value as Address
    this.persistToToken(value)
  }

  setInputToken(token: TokenStore | undefined): void {
    if (!token) return
    this.setFromTokenFromUi(token.address.toViemAddress())
  }

  setOutputToken(token: TokenStore | undefined): void {
    if (!token) return
    this.setToTokenFromUi(token.address.toViemAddress())
  }

  setInputAmount(amount: string): void {
    this.inputAmount = amount
  }

  switchTokens(): void {
    const fromSources = { ...this.fromTokenParamSources }
    this.fromTokenParamSources = { ...this.toTokenParamSources }
    this.toTokenParamSources = fromSources

    this.persistCurrentTokenAddresses()
  }

  private resolveToken(address: string | undefined): TokenStore | undefined {
    if (!address) return undefined

    if (this.nativeToken.address.equalsString(address)) {
      return ETH
    }

    const tokenAddress = LcAddress.tryCreate(address)
    if (!tokenAddress) return undefined

    const found = this.tokenRepository.find(tokenAddress)
    if (found) return found

    if (WETH.address.equalsString(address)) return WETH
    if (SCALE.address.equalsString(address)) return SCALE

    return undefined
  }

  private persistCurrentTokenAddresses(): void {
    if (this.fromToken) {
      this.persistFromToken(this.fromToken)
    }

    if (this.toToken) {
      this.persistToToken(this.toToken)
    }
  }

  private persistFromToken(value: string): void {
    this.storage.setItem(`${this.chainSlug}-swap-from-address`, value)
    this.urlParams.setParam("sell", value)
  }

  private persistToToken(value: string): void {
    this.storage.setItem(`${this.chainSlug}-swap-to-address`, value)
    this.urlParams.setParam("buy", value)
  }
}
